import get from 'lodash.get';
import { ArrayHelperService } from 'ui-components/services/array-helper-service';
import { allSelectionComponentMap } from './all-selection-component-map';
import { multiSelectionComponentMap } from './multi-selection-component-map';
import { singleSelectionComponentMap } from './single-selection-component-map';
import { singleFulfillmentPlanComponents } from './single-fulfillment-plan-components';

const v2MultiModulesSettingName = 'Orders:Drawer:MultiModules';
const v2SingleModulesSettingName = 'Orders:Drawer:SingleModules';
const v3MultiModulesSettingName = `v3.${v2MultiModulesSettingName}`;
const v3SingleModulesSettingName = `v3.${v2SingleModulesSettingName}`;

class OrdersDrawerServiceClass {
  getV2MultiModuleSettingName() {
    return v2MultiModulesSettingName;
  }

  getV3MultiModuleSettingName() {
    return v3MultiModulesSettingName;
  }

  getV2SingleModuleSettingName() {
    return v2SingleModulesSettingName;
  }

  getV3SingleModuleSettingName() {
    return v3SingleModulesSettingName;
  }

  getDefaultMultiModuleCollection() {
    return multiSelectionComponentMap;
  }

  getDefaultSingleFulfillmentPlanComponents(isRedesignEnabled = false) {
    return isRedesignEnabled ? singleFulfillmentPlanComponents : singleSelectionComponentMap;
  }

  getModuleCollectionByPropertyName(propName, moduleNames = [], moduleCollection = []) {
    const modules = moduleNames.reduce((acc, moduleName) => {
      const moduleToPush = moduleCollection.find((module) => module[propName] === moduleName);
      if (moduleToPush) {
        acc.push(moduleToPush);
      }
      return acc;
    }, []);

    if (modules.length !== moduleCollection.length) {
      // a new thing was added to the component map, add it to the collection
      const excludedModules = moduleCollection.filter((module) => !modules.includes(module));
      return [...modules, ...excludedModules];
    }
    return modules;
  }

  getAllSelectedOrderedModules() {
    return allSelectionComponentMap;
  }

  getMultiSelectionOrderedModules(moduleNames = []) {
    if (!moduleNames || moduleNames.length === 0) {
      return this.getDefaultMultiModuleCollection();
    }
    return this.getModuleCollectionByPropertyName('name', moduleNames, multiSelectionComponentMap);
  }

  getSingleFulfillmentPlanOrderedComponents(moduleNames = [], isRedesignEnabled = false) {
    const defaultComponents = this.getDefaultSingleFulfillmentPlanComponents(isRedesignEnabled);

    return !moduleNames?.length ? defaultComponents : this.getModuleCollectionByPropertyName('name', moduleNames, defaultComponents);
  }

  getMultiModulesFromUserSettings(userSettings = []) {
    const v3MultiModuleSetting = userSettings.find((setting) => setting.settingName === v3MultiModulesSettingName);
    if (v3MultiModuleSetting) {
      const settingAsArray = ArrayHelperService.parseStringToArrayOrUndefined(get(v3MultiModuleSetting, 'settingValue', []));
      return settingAsArray ? this.getMultiSelectionOrderedModules(settingAsArray) : this.getDefaultMultiModuleCollection();
    }
    const v2MultiModuleSetting = userSettings.find((setting) => setting.settingName === v2MultiModulesSettingName);
    if (v2MultiModuleSetting) {
      const settingAsArray = ArrayHelperService.parseStringToArrayOrUndefined(get(v2MultiModuleSetting, 'settingValue', []));
      return settingAsArray
        ? this.getModuleCollectionByPropertyName('v2Name', settingAsArray, multiSelectionComponentMap)
        : this.getDefaultMultiModuleCollection();
    }
    return this.getDefaultMultiModuleCollection();
  }

  getSingleModulesFromUserSettings(userSettings = [], isRedesignEnabled = false) {
    const v3SingleModuleSetting = userSettings.find((setting) => setting.settingName === v3SingleModulesSettingName);
    if (v3SingleModuleSetting) {
      const settingsAsArray = ArrayHelperService.parseStringToArrayOrUndefined(get(v3SingleModuleSetting, 'settingValue', []));

      return this.getSingleFulfillmentPlanOrderedComponents(settingsAsArray, isRedesignEnabled);
    }

    const defaultComponents = this.getDefaultSingleFulfillmentPlanComponents(isRedesignEnabled);

    const v2SingleModuleSetting = userSettings.find((setting) => setting.settingName === v2SingleModulesSettingName);
    if (v2SingleModuleSetting) {
      const settingsAsArray = ArrayHelperService.parseStringToArrayOrUndefined(get(v2SingleModuleSetting, 'settingValue', []));

      return settingsAsArray ? this.getModuleCollectionByPropertyName('v2Name', settingsAsArray, defaultComponents) : defaultComponents;
    }

    return defaultComponents;
  }

  changeOrderInArray(array, reorderAction) {
    const sourceIndex = get(reorderAction, 'source.index');
    const destinationIndex = get(reorderAction, 'destination.index');
    return ArrayHelperService.reorder(array, sourceIndex, destinationIndex);
  }
}

export const OrdersDrawerService = new OrdersDrawerServiceClass();
